import React, { useState, useRef } from 'react';
import { 
  BoldIcon, 
  ItalicIcon, 
  UnderlineIcon,
  ListBulletIcon,
  NumberedListIcon,
  EyeIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline';

interface HTMLEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  height?: string;
  readonly?: boolean;
}

export const HTMLEditor: React.FC<HTMLEditorProps> = ({
  value,
  onChange,
  placeholder = 'Wprowadź treść...',
  height = '300px',
  readonly = false
}) => {
  const [showSource, setShowSource] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const insertHtml = (tag: string, needsClosing = true) => {
    // Przełącz na tryb kodu źródłowego jeśli nie jest aktywny
    if (!showSource) {
      setShowSource(true);
    }
    
    // Odczekaj na renderowanie textarea
    setTimeout(() => {
      if (!textareaRef.current) return;
      
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = value.substring(start, end);
      
      let newText;
      if (needsClosing) {
        newText = value.substring(0, start) + 
                  `<${tag}>${selectedText}</${tag}>` + 
                  value.substring(end);
      } else {
        newText = value.substring(0, start) + `<${tag}>` + value.substring(end);
      }
      
      onChange(newText);
      
      // Restore focus and cursor position
      setTimeout(() => {
        textarea.focus();
        const newPos = needsClosing ? start + tag.length + 2 + selectedText.length : start + tag.length + 2;
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    }, 100);
  };

  const toolbarButtons = [
    { icon: BoldIcon, action: () => insertHtml('strong'), title: 'Pogrubienie' },
    { icon: ItalicIcon, action: () => insertHtml('em'), title: 'Kursywa' },
    { icon: UnderlineIcon, action: () => insertHtml('u'), title: 'Podkreślenie' },
    { icon: ListBulletIcon, action: () => insertHtml('ul><li', false), title: 'Lista punktowana' },
    { icon: NumberedListIcon, action: () => insertHtml('ol><li', false), title: 'Lista numerowana' },
  ];

  if (readonly) {
    return (
      <div 
        className="border rounded-md p-3 bg-gray-50"
        style={{ minHeight: height }}
        dangerouslySetInnerHTML={{ __html: value }}
      />
    );
  }

  return (
    <div className="border border-gray-300 rounded-md">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50">
        {toolbarButtons.map((button, index) => (
          <button
            key={index}
            type="button"
            onClick={button.action}
            className="p-1.5 hover:bg-gray-200 rounded transition-colors"
            title={button.title}
          >
            <button.icon className="h-4 w-4 text-gray-600" />
          </button>
        ))}
        
        <div className="h-4 w-px bg-gray-300 mx-1" />
        
        <button
          type="button"
          onClick={() => insertHtml('h2')}
          className="px-2 py-1 text-sm hover:bg-gray-200 rounded transition-colors"
          title="Nagłówek"
        >
          H2
        </button>
        
        <button
          type="button"
          onClick={() => insertHtml('p')}
          className="px-2 py-1 text-sm hover:bg-gray-200 rounded transition-colors"
          title="Paragraf"
        >
          P
        </button>
        
        <div className="h-4 w-px bg-gray-300 mx-1" />
        
        <button
          type="button"
          onClick={() => setShowSource(!showSource)}
          className={`p-1.5 rounded transition-colors ${showSource ? 'bg-green-100 text-green-600' : 'hover:bg-gray-200'}`}
          title={showSource ? "Przełącz na podgląd" : "Edytuj kod HTML"}
        >
          {showSource ? (
            <EyeIcon className="h-4 w-4" />
          ) : (
            <CodeBracketIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Editor Area */}
      <div style={{ minHeight: height }}>
        {showSource ? (
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full p-3 border-0 resize-none focus:outline-none font-mono text-sm"
            style={{ minHeight: height }}
            spellCheck={false}
          />
        ) : (
          <div 
            className="p-3 prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-em:text-gray-700 prose-ul:text-gray-700 prose-ol:text-gray-700"
            style={{ 
              minHeight: height,
              lineHeight: '1.6',
              fontSize: '14px'
            }}
            dangerouslySetInnerHTML={{ 
              __html: value || `<p class="text-gray-400 italic">${placeholder}</p>` 
            }}
          />
        )}
      </div>
      
      {/* Quick Help */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
        {showSource ? (
          <span>Tryb edycji HTML - Użyj przycisków powyżej lub wprowadź kod HTML bezpośrednio</span>
        ) : (
          <span>Tryb podglądu - Kliknij przycisk &lt;/&gt; aby edytować kod HTML lub użyj przycisków formatowania</span>
        )}
      </div>
    </div>
  );
};

export default HTMLEditor;